<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed } from "vue"
import { useI18n } from "vue-i18n"
import { RouterLink, useRoute } from "vue-router"
import { useConfigStore } from "@core/stores/config.store"
import { useAuthStore } from "@core/stores/auth.store"

const { t } = useI18n({
    messages: {
        fr: {
            home: "Accueil",
            appName: "Homeys",
            energyManager: "Energy Manager",
            buildings: "Bâtiments",
            sources: "Objets connectés",
            optimisationContractuelle: "Optimisation contractuelle",
            myDashboards: "Mes tableaux de bord",
            dashboardGlobal: "Tableau de bord global",
            dashboardIndoorTemp:
                "Analyse de la température ambiante des bâtiments",
            lockHintDashboardIndoorTemp:
                "Souscrivez au module Exploitant chauffagiste pour accéder à ce tableau de bord.",
        },
    },
})

const configStore = useConfigStore()
const authStore = useAuthStore()
const route = useRoute()

/**
 * Logo blanc du scope (fallback sur le nom de l'app si absent)
 */
const scopeLogoFullWhite = computed(() => configStore.getScopeLogoFullWhite)

/**
 * Couleur primaire du scope pour le header (fallback blanc)
 */
const headerBackgroundColor = computed(
    () => configStore.getScopePrimaryColor ?? "#ffffff"
)

/**
 * Lien de redirection du logo
 * - Si l'utilisateur a l'offre Energy Manager : /energy-manager/dashboard
 * - Sinon : /building
 */
const logoLink = computed(() => {
    return authStore.hasOfferEnergyManager && authStore.isEmployeWriteOrHigher
        ? "/energy-manager/dashboard"
        : "/building"
})

// ================================
// MENU ITEMS (Menubar)
// ================================
type HeaderMenubarLeaf = {
    label: string
    icon?: string
    route: { path: string }
    disabled?: boolean
    /** Clé i18n du tooltip explicatif lorsque l’entrée est verrouillée (`disabled`). */
    lockHintKey?: "lockHintDashboardIndoorTemp"
}

type HeaderMenubarParent = {
    label: string
    icon?: string
    items: HeaderMenubarLeaf[]
}

type HeaderMenubarItem = HeaderMenubarLeaf | HeaderMenubarParent

function isMenubarParent(item: HeaderMenubarItem): item is HeaderMenubarParent {
    return Array.isArray((item as HeaderMenubarParent).items)
}

const menubarItems = computed((): HeaderMenubarItem[] => {
    const items: HeaderMenubarItem[] = []

    const canAccessEmDashboard =
        authStore.hasOfferEnergyManager && authStore.isEmployeWriteOrHigher

    if (canAccessEmDashboard) {
        const canAccessIotDashboard =
            authStore.hasModuleExploitantChauffagiste &&
            authStore.isEmployeWriteOrHigher

        const dashboardItems = [
            {
                label: t("dashboardGlobal"),
                route: { path: "/energy-manager/dashboard" },
                disabled: !canAccessEmDashboard,
            },
            {
                label: t("dashboardIndoorTemp"),
                route: { path: "/energy-manager/dashboard/iot" },
                disabled: !canAccessIotDashboard,
                lockHintKey: "lockHintDashboardIndoorTemp",
            },
        ] as HeaderMenubarLeaf[]

        // Optimisation contractuelle (dashboard parc)
        // Aligné sur `beforeEnterDashboardOptimTurpe` : `hasModuleOptimTurpeDashboard` + employé write+
        if (
            authStore.hasModuleOptimTurpeDashboard &&
            authStore.isEmployeWriteOrHigher
        ) {
            dashboardItems.push({
                label: t("optimisationContractuelle"),
                route: { path: "/dashboard/optimisation-contractuelle" },
            })
        }

        items.push({
            label: t("myDashboards"),
            icon: "pi pi-table",
            items: dashboardItems,
        })
    }

    // Bâtiments / Dossiers
    items.push({
        label: t("buildings"),
        icon: "pi pi-building",
        route: { path: "/building" },
    })

    // Sources / Capteurs
    items.push({
        label: t("sources"),
        icon: "pi pi-sitemap",
        route: { path: "/source" },
    })

    return items
})

// ================================
// LOGIQUE MÉTIER
// ================================

/**
 * Vérifie si un item du menu est actif en fonction de la route courante
 * @param item - L'item du menu à vérifier
 * @returns true si l'item correspond à la route courante
 */
function isItemActive(item: HeaderMenubarItem): boolean {
    if (isMenubarParent(item)) {
        return item.items.some((sub) => isItemActive(sub))
    }
    if (item.route?.path) {
        return route.path === item.route.path
    }
    return false
}
</script>

<template>
    <header
        class="relative shrink-0 border-b border-surface-200 shadow-[0_2px_8px_rgba(0,0,0,0.06)]"
        :style="{ backgroundColor: headerBackgroundColor }"
    >
        <BaseMenubar
            :model="menubarItems"
            :style="{
                '--p-menubar-mobile-button-hover-background': 'transparent',
            }"
            class="menubar-navigation"
            breakpoint="80rem"
            :pt="{
                root: {
                    class: 'bg-transparent!',
                },
                itemlink: {
                    class: 'rounded',
                },
                itemContent: ({
                    context,
                }: {
                    context: { focused: boolean }
                }) => ({
                    class: [
                        'hover:bg-surface-100 xl:hover:bg-white/10! rounded',
                        {
                            'bg-surface-100 xl:bg-white/10!': context.focused,
                        },
                    ],
                }),
                button: {
                    class: 'text-white! hover:bg-white/10!',
                },
            }"
        >
            <template #start>
                <div
                    class="absolute left-0 right-0 flex justify-center md:static md:block"
                >
                    <RouterLink
                        :to="logoLink"
                        class="flex items-center gap-2 font-semibold text-surface-700 no-underline hover:text-primary-700"
                    >
                        <img
                            v-if="scopeLogoFullWhite"
                            :src="scopeLogoFullWhite"
                            :alt="
                                configStore.getScopeBrand ??
                                configStore.getScopeName ??
                                t('appName')
                            "
                            class="h-8 object-contain object-left"
                        />
                        <span v-else>{{ t("appName") }}</span>
                    </RouterLink>
                </div>
            </template>
            <template #item="{ item, props: itemProps, hasSubmenu, root }">
                <a
                    v-if="hasSubmenu"
                    v-ripple
                    class="app-header__menuitem flex items-center gap-2 text-primary-900 md:text-white hover:bg-surface-100 md:hover:bg-white/20 rounded"
                    :class="[
                        root === true
                            ? 'xl:hover:bg-white/10! hover:bg-primary-50!'
                            : '',
                        {
                            'bg-primary-50! xl:bg-white/20!':
                                isItemActive(item),
                        },
                    ]"
                    v-bind="itemProps.action"
                >
                    <i v-if="item.icon" :class="item.icon" />
                    <span>{{ item.label }}</span>
                    <i class="pi pi-angle-down ml-auto" />
                </a>
                <RouterLink
                    v-else-if="item.route && !item.disabled"
                    v-slot="{ href, navigate }"
                    :to="item.route"
                    custom
                >
                    <a
                        v-ripple
                        :href="href"
                        class="app-header__menuitem flex items-center gap-2"
                        :class="[
                            root === false
                                ? 'text-primary-900! hover:bg-primary-50!'
                                : 'xl:hover:bg-white/10! hover:bg-primary-50!',
                            {
                                'bg-surface-200 md:bg-white/20!':
                                    isItemActive(item),
                            },
                        ]"
                        v-bind="itemProps.action"
                        @click="navigate"
                    >
                        <i v-if="item.icon" :class="item.icon" />
                        <span>{{ item.label }}</span>
                    </a>
                </RouterLink>
                <div
                    v-else-if="item.route && item.disabled"
                    class="app-header__menuitem flex items-center gap-2 rounded opacity-60 p-[var(--p-menubar-item-padding)]"
                    :class="
                        root === false
                            ? '!text-primary-900 xl:hover:bg-primary-50!'
                            : ''
                    "
                >
                    <span class="cursor-not-allowed text-surface-600">{{
                        item.label
                    }}</span>
                    <!--
                      Les items disabled ont .p-disabled (pointer-events: none sur le thème).
                      Le tooltip doit cibler un enfant en pointer-events-auto pour recevoir le survol.
                    -->

                    <span
                        v-if="item.lockHintKey"
                        class="header-nav-lock-trigger pointer-events-auto inline-flex shrink-0 cursor-help rounded-sm p-0.5 -m-0.5"
                        v-tooltip.bottom="{
                            value: t(item.lockHintKey),
                            pt: {
                                root: 'z-[3000]!',
                                text: 'text-sm max-w-xs leading-normal',
                            },
                        }"
                        role="img"
                        :aria-label="t(item.lockHintKey)"
                        tabindex="0"
                    >
                        <i
                            class="pi pi-lock text-xs text-surface-600"
                            aria-hidden="true"
                        />
                    </span>
                    <i
                        v-else
                        class="pi pi-lock shrink-0 text-xs text-surface-600"
                        aria-hidden="true"
                    />
                </div>
            </template>
            <template #end>
                <div class="flex items-center gap-4">
                    <HeaderSearch />
                    <HeaderProfileMenu />
                </div>
            </template>
        </BaseMenubar>
    </header>
</template>

<style scoped>
.menubar-navigation {
    --p-menubar-item-color: var(--p-primary-900);
}
@media (min-width: 80rem) {
    .menubar-navigation {
        --p-menubar-color: white;
        --p-menubar-item-color: white;
        --p-menubar-item-active-background: rgb(255 255 255 / 0.2);
        --p-menubar-item-active-color: white;
        --p-menubar-item-focus-color: white;
        --p-menubar-item-focus-background: rgb(255 255 255 / 0.2);
    }
}

/* Item menubar PrimeVue .p-disabled : rétablit le survol pour le déclencheur du tooltip (cadenas). */
.menubar-navigation :deep(.p-menubar-item.p-disabled .header-nav-lock-trigger) {
    pointer-events: auto;
    position: relative;
    z-index: 1;
}
</style>
